// HomeownerCommsStrip.tsx (Oct 20, 2026)
//
// The top of the deal's Comms tab: one chip per automatic text or email the homeowner-comms engine
// queued, sent or skipped for this deal (homeowner_comm_sends), with the words on hover. A chip opens
// its details; admin and finance get Send now (a waiting one) or Resend (a finished one), and Log
// feedback rating for homeowners who answered "how did we do" by text or on the phone (it writes
// deals.feedback_rating, so the engine's reply follows as if they had tapped the stars).

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase, SUPABASE_URL } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { fetchEdge, readEdgeJson } from "../../lib/edgeError";
import {
  COMMS_STAFF_ROLES,
  groupChips,
  skipText,
  staffAction,
  statusOf,
  touchLabel,
  type CommChip,
  type CommSendRow,
  type Tone,
} from "../../lib/homeownerComms";

const ROW_COLS = "id,comm_key,ref,status,skip_reason,source,scheduled_for,sent_at,sms_status,email_status,rendered,error,created_at";

const TONE_CLASS: Record<Tone, string> = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  waiting: "bg-sky-50 text-sky-700 border-sky-200",
  held: "bg-amber-50 text-amber-700 border-amber-200",
  skipped: "bg-slate-50 text-slate-500 border-slate-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  shadow: "bg-violet-50 text-violet-700 border-violet-200",
};

const TONE_ICON: Record<Tone, string> = {
  sent: "✓",
  waiting: "⏳",
  held: "⏸",
  skipped: "⏭",
  failed: "✗",
  shadow: "◌",
};

const MODE_NOTE: Record<string, string> = {
  off: "Automatic texts are switched off: nothing new goes out.",
  shadow: "Automatic texts are in practice mode: they are written out here but not sent.",
  test: "Automatic texts are in test mode: only the test deals send.",
};

const RATING_EFFECT: Record<number, string> = {
  5: "texts them the Google review link",
  4: "texts them a thank-you",
  3: "texts them that their RM will call, and alerts the RM and Chad",
  2: "texts them that their RM will call, and alerts the RM and Chad",
  1: "texts them that their RM will call, and alerts the RM and Chad",
};

function fmtTime(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

interface DealBits {
  feedback_rating: number | null;
  build_completed_date: string | null;
  homeowner_name: string | null;
}

interface SendResult {
  ok?: boolean;
  status?: string;
  skip?: string;
  requeue_at?: string;
  shadow?: boolean;
  held?: boolean;
  error?: string;
  detail?: string;
}

export default function HomeownerCommsStrip({ dealId }: { dealId: string }) {
  const { role, isSuperUser, member } = useAuth();
  const canAct = isSuperUser || COMMS_STAFF_ROLES.has(role ?? "");

  const [rows, setRows] = useState<CommSendRow[] | null>(null);
  const [mode, setMode] = useState<string | null>(null);
  const [deal, setDeal] = useState<DealBits | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [ratingOpen, setRatingOpen] = useState(false);
  const [ratingPick, setRatingPick] = useState<number | null>(null);
  const [ratingNote, setRatingNote] = useState("");

  const load = useCallback(async () => {
    const [r, m, d] = await Promise.all([
      supabase.from("homeowner_comm_sends").select(ROW_COLS).eq("deal_id", dealId).order("created_at", { ascending: true }).limit(300),
      supabase.from("finance_settings").select("value_jsonb").eq("key", "hc_mode").maybeSingle(),
      supabase.from("deals").select("feedback_rating,build_completed_date,homeowner_name").eq("id", dealId).maybeSingle(),
    ]);
    if (!r.error) setRows((r.data ?? []) as CommSendRow[]);
    if (!m.error) setMode(typeof m.data?.value_jsonb === "string" ? (m.data.value_jsonb as string) : "off");
    if (!d.error) setDeal((d.data as DealBits | null) ?? null);
  }, [dealId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const ch = supabase
      .channel(`hc-strip-${dealId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "homeowner_comm_sends", filter: `deal_id=eq.${dealId}` }, () => void load())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "deals", filter: `id=eq.${dealId}` }, () => void load())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [dealId, load]);

  const chips = useMemo(() => groupChips(rows ?? []), [rows]);
  const open = chips.find((c) => c.key === openKey) ?? null;
  const showRating = !!deal?.build_completed_date;

  if (rows === null) return null;
  if (chips.length === 0 && !showRating) return null;

  async function act(chip: CommChip) {
    const action = staffAction(chip.row);
    if (!action) return;
    const force = action === "resend";
    if (force && !confirm(`Send "${touchLabel(chip.row).long}" to the homeowner again?`)) return;
    setBusy(chip.row.id);
    setMsg(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetchEdge(`${SUPABASE_URL}/functions/v1/homeowner-comms`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
        body: JSON.stringify({ mode: "send", deal_id: dealId, row_id: chip.row.id, force }),
      });
      const out = await readEdgeJson<SendResult>(res);
      if (!res.ok || !out.ok) throw new Error(out.detail ?? out.error ?? `HTTP ${res.status}`);
      setMsg(
        out.status === "sent" ? { ok: true, text: "Sent." }
        : out.status === "partial" ? { ok: true, text: "Partly sent; see the chip for which part didn't go." }
        : out.skip ? { ok: false, text: `Didn't go: ${skipText({ comm_key: chip.row.comm_key, skip_reason: out.skip })}.` }
        : out.requeue_at ? { ok: true, text: `Waiting until ${fmtTime(out.requeue_at)}: texts go out 8 AM to 7 PM Eastern.` }
        : out.shadow ? { ok: false, text: "Not sent: automatic texts are in practice or test mode." }
        : { ok: true, text: `Done (${out.status ?? "processed"}).` },
      );
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(null);
      void load();
    }
  }

  async function logRating() {
    if (!ratingPick) return;
    setBusy("rating");
    setMsg(null);
    const { error } = await supabase
      .from("deals")
      .update({ feedback_rating: ratingPick, feedback_date: new Date().toISOString(), ...(ratingNote.trim() ? { feedback_comment: ratingNote.trim() } : {}) })
      .eq("id", dealId);
    if (error) {
      setMsg({ ok: false, text: `Couldn't log the rating: ${error.message}` });
    } else {
      const { error: logErr } = await supabase.from("activity_log").insert({
        deal_id: dealId, activity_type: "system_event", performed_by: member?.id ?? null,
        title: `Feedback logged: ${ratingPick} star${ratingPick === 1 ? "" : "s"}`,
        description: `Logged from the Comms tab (the homeowner answered by text or phone)${ratingNote.trim() ? `: "${ratingNote.trim()}"` : ""}.`,
        metadata: { source: "homeowner_comms_strip", rating: ratingPick },
      });
      if (logErr) console.warn("feedback activity_log insert failed:", logErr.message);
      setRatingOpen(false);
      setMsg({ ok: true, text: `Logged ${ratingPick} star${ratingPick === 1 ? "" : "s"}. The reply goes out within about 5 minutes (8 AM to 7 PM).` });
    }
    setBusy(null);
    void load();
  }

  return (
    <div className="bg-white rounded-xl shadow px-4 py-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-bardin-ink">🤖 Automatic texts &amp; emails</div>
        {mode && mode !== "on" && <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-0.5">{MODE_NOTE[mode] ?? mode}</div>}
      </div>

      {chips.length === 0 ? (
        <div className="text-xs text-slate-400">None on this deal yet.</div>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {chips.map((c) => {
            const st = statusOf(c.row);
            const label = touchLabel(c.row);
            const sms = typeof c.row.rendered?.sms === "string" ? (c.row.rendered.sms as string) : null;
            const hover = [
              `${label.long}: ${st.label}${c.row.status === "skipped" || c.row.status === "held" ? ` (${skipText(c.row)})` : ""}`,
              sms ? `\n\n${sms}` : "",
              typeof c.row.rendered?.email_subject === "string" ? `\n\nEmail: ${c.row.rendered.email_subject}` : "",
            ].join("");
            return (
              <li key={c.key}>
                <button
                  type="button"
                  onClick={() => setOpenKey(openKey === c.key ? null : c.key)}
                  title={hover}
                  className={
                    "px-2 py-0.5 rounded-full border text-[11px] font-medium transition " + TONE_CLASS[st.tone] +
                    (openKey === c.key ? " ring-2 ring-bardin-teal/40" : " hover:brightness-95")
                  }
                >
                  {TONE_ICON[st.tone]} {label.short}
                  {c.resends > 0 && <span className="opacity-70"> ×{c.resends + 1}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {open && <ChipDetail chip={open} canAct={canAct} busy={busy === open.row.id} onAct={() => void act(open)} />}

      {showRating && (
        <div className="text-xs text-slate-600 border-t border-slate-100 pt-2">
          {deal?.feedback_rating != null ? (
            <span>
              Feedback: <span className="text-amber-500">{"★".repeat(deal.feedback_rating)}</span>
              <span className="text-slate-300">{"★".repeat(Math.max(0, 5 - deal.feedback_rating))}</span> ({deal.feedback_rating}/5)
            </span>
          ) : !canAct ? (
            <span className="text-slate-400">Not rated yet.</span>
          ) : !ratingOpen ? (
            <button
              type="button"
              onClick={() => { setRatingOpen(true); setRatingPick(null); setRatingNote(""); }}
              className="px-2.5 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
              title="For homeowners who answered by text or on the phone instead of tapping the stars"
            >
              ☆ Log feedback rating
            </button>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <span className="mr-1">They rated the build:</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRatingPick(n)}
                    className={"text-lg leading-none " + (ratingPick && n <= ratingPick ? "text-amber-500" : "text-slate-300 hover:text-amber-300")}
                    aria-label={`${n} star${n === 1 ? "" : "s"}`}
                  >
                    ★
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={ratingNote}
                onChange={(e) => setRatingNote(e.target.value)}
                placeholder="What they said (optional)"
                className="w-full border border-slate-300 rounded px-2 py-1"
              />
              {ratingPick && (
                <div className="text-[11px] text-slate-500">
                  {ratingPick} star{ratingPick === 1 ? "" : "s"} {RATING_EFFECT[ratingPick]}, the same as tapping the stars themselves. It only happens once per deal.
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void logRating()}
                  disabled={!ratingPick || busy === "rating"}
                  className="px-3 py-1 rounded-md bg-bardin-teal text-white font-semibold disabled:opacity-40"
                >
                  {busy === "rating" ? "Saving…" : "Log rating"}
                </button>
                <button type="button" onClick={() => setRatingOpen(false)} className="px-3 py-1 rounded-md border border-slate-200 text-slate-600">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {msg && (
        <div className={"text-[11px] rounded px-2 py-1 " + (msg.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")}>{msg.text}</div>
      )}
    </div>
  );
}

function ChipDetail({ chip, canAct, busy, onAct }: { chip: CommChip; canAct: boolean; busy: boolean; onAct: () => void }) {
  const r = chip.row;
  const st = statusOf(r);
  const label = touchLabel(r);
  const sms = typeof r.rendered?.sms === "string" ? (r.rendered.sms as string) : null;
  const subject = typeof r.rendered?.email_subject === "string" ? (r.rendered.email_subject as string) : null;
  const action = staffAction(r);
  const line =
    r.status === "sent" || r.status === "partial"
      ? `${st.label} ${fmtTime(r.sent_at)}${r.sms_status ? ` · text ${r.sms_status === "sent" ? "✓" : r.sms_status.replace(/_/g, " ")}` : ""}${r.email_status ? ` · email ${r.email_status === "sent" ? "✓" : r.email_status.replace(/_/g, " ")}` : ""}`
      : r.status === "queued"
      ? r.scheduled_for && Date.parse(r.scheduled_for) > Date.now() ? `Goes out ${fmtTime(r.scheduled_for)}` : "Goes out within about 5 minutes"
      : r.status === "claimed" ? "Going out now"
      : r.status === "held" ? `Held: ${skipText(r)}`
      : r.status === "skipped" ? `Skipped: ${skipText(r)}`
      : r.status === "failed" ? `Failed${r.error ? `: ${r.error}` : ""}`
      : r.status === "shadow" ? "Written out but not sent (practice or test mode)"
      : st.label;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-semibold text-slate-800">{label.long}</span>
          <span className="text-slate-500"> · {line}</span>
          {chip.resends > 0 && <span className="text-slate-400"> · sent again {chip.resends}×</span>}
        </div>
        {canAct && action && (
          <button
            type="button"
            onClick={onAct}
            disabled={busy}
            className="px-2.5 py-1 rounded-md border border-bardin-teal/40 text-bardin-teal bg-white hover:bg-bardin-teal/10 font-semibold disabled:opacity-40"
          >
            {busy ? "Sending…" : action === "send_now" ? "Send now" : "Resend"}
          </button>
        )}
      </div>
      {sms && <div className="whitespace-pre-wrap rounded-lg bg-white border border-slate-200 px-2.5 py-1.5 text-slate-800">{sms}</div>}
      {subject && <div className="text-slate-600">✉️ Email: {subject}</div>}
      {!sms && !subject && r.status === "queued" && <div className="text-slate-400">The words are filled in when it goes out.</div>}
    </div>
  );
}
